"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

const faqs = [
  {
    question: "What is ChickyTutor for?",
    answer:
      "ChickyTutor is built for spoken practice. You choose a language and level, talk with the AI tutor, and ask for help when a sentence does not come out the way you want.",
  },
  {
    question: "Which languages can I practise?",
    answer:
      "ChickyTutor currently supports 70+ languages. The language picker shows the full available list, including Spanish, French, Dutch, German, Italian, Portuguese, Japanese, Korean, Mandarin, Cantonese, Arabic, Hindi, and Thai.",
  },
  {
    question: "Can I try it before I subscribe?",
    answer:
      "Yes. The guest demo on this page gives you a five-minute voice session and does not require a card. A free account currently includes 10 minutes of speaking practice per day. If a paid plan includes a trial, you will see it on that plan before checkout.",
  },
  {
    question: "What kind of feedback can I ask for?",
    answer:
      "You can ask the tutor to check pronunciation, grammar, wording, or how natural a reply sounds. AI feedback can make mistakes, so use it as practice support rather than an official assessment.",
  },
  {
    question: "Can I cancel a subscription?",
    answer:
      "Yes. You can cancel your subscription and keep access until the end of the current billing period. The checkout shows the plan price and any available trial before you pay.",
  },
  {
    question: "Does it work on mobile?",
    answer:
      "Yes. ChickyTutor works in modern browsers on phones, tablets, and computers. You can also install the web app on a supported device for quicker access from your home screen.",
  },
];

export function LandingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const shouldReduceMotion = useReducedMotion();

  return (
    <section id="faq" className="bg-white py-16 md:py-24" aria-labelledby="faq-title">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          className="mb-10 max-w-2xl md:mb-12"
        >
          <h2
            id="faq-title"
            className="text-3xl font-bold tracking-[-0.035em] text-[#1C1C1E] md:text-5xl"
          >
            Questions before you speak
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            The practical details about the demo, free use, and paid plans.
          </p>
        </motion.div>

        <div className="rounded-2xl border border-gray-200 bg-[#F5F5F0] px-5 sm:px-7">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const triggerId = `landing-faq-trigger-${index}`;
            const panelId = `landing-faq-panel-${index}`;

            return (
              <div key={faq.question} className="border-b border-gray-300 last:border-b-0">
                <h3>
                  <button
                    id={triggerId}
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full items-center justify-between gap-5 py-5 text-left font-bold text-[#1C1C1E] transition-colors hover:text-[#A8431F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5F5F0]"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </h3>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      initial={shouldReduceMotion ? false : { height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={shouldReduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-[68ch] pb-5 leading-7 text-gray-600">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
